/**
 * Horizon overrides for Shopify.actions:
 * - updateCart: emit events from the cart drawer scope.
 * - openCart: open the cart drawer when explicitly requested (header icon, etc.).
 *
 * ATC behaviour:
 * - Always AJAX (native /cart/add forms are intercepted).
 * - Cart drawer content updates via cartLinesUpdate — do NOT auto-open.
 * - Show a centered toast with a product thumbnail for 3s.
 *
 * Request budget: one round trip. /cart/add.js is asked for the cart section
 * HTML in the same POST, so the confirmation and the repaint both come out of
 * that single response. The follow-up POST /cart/update.js this used to make —
 * a second full section render — is gone, and the cart read that trues up
 * quantity selectors no longer sits in front of the toast.
 */

import { morphSection } from '@theme/section-renderer';

const CART_LINES_UPDATE = 'shopify:cart:lines-update';
const TOAST_MS = 3000;
const TOAST_LEAVE_MS = 460;
const TOAST_MESSAGE = 'Product added to cart';
const TOAST_DEDUP_MS = 600;
const EARLY_ADD_EVENT = 'tw:cart:added';
const EARLY_ADD_WINDOW_MS = 4000;
const TOAST_THUMB_WIDTH = 160;
const ACTIONS_RETRY_MS = 50;
const ACTIONS_RETRY_MAX = 40;
const PENDING_FAILSAFE_MS = 10000;

/**
 * Theme.routes is declared as a top-level `const Theme` in scripts.liquid, which
 * creates a global *lexical* binding — it is reachable as a bare identifier but
 * never as window.Theme. Reading only window.Theme silently fell through to the
 * hardcoded defaults, which breaks locale-prefixed routes such as
 * /en-de/cart/add.js.
 * @param {string} name
 * @param {string} fallback
 * @returns {string}
 */
function themeRoute(name, fallback) {
  try {
    // eslint-disable-next-line no-undef
    const route = Theme?.routes?.[name];
    if (typeof route === 'string' && route) return route;
  } catch {
    // No global Theme on this page.
  }
  const fromWindow = window.Theme?.routes?.[name];
  return typeof fromWindow === 'string' && fromWindow ? fromWindow : fallback;
}

/** @type {number} */
let lastToastAt = 0;
/** @type {string} */
let lastToastMessage = '';
/** @type {number} */
let toastHideTimer = 0;
/** @type {number} */
let toastRemoveTimer = 0;
/** @type {boolean} */
let toastBound = false;
/** @type {boolean} */
let actionsConfigured = false;
/** @type {boolean} */
let formInterceptorBound = false;
/** @type {Promise<void>} */
let cartSyncQueue = Promise.resolve();
/**
 * Storefront cart mutations 400 on this shop. Once that is proven, stop paying
 * for the failing round trip on every later add.
 * @type {boolean}
 */
let storefrontCartBroken = false;
/** @type {number} */
let earlyAddAt = 0;

/**
 * @returns {Promise<HTMLElement & { open?: () => void; isOpen?: boolean } | null>}
 */
async function getCartDrawer() {
  /** @type {HTMLElement & { open?: () => void; isOpen?: boolean } | null} */
  let drawer = document.querySelector('theme-drawer#cart-drawer');
  if (!drawer) return null;

  if (typeof drawer.open !== 'function') {
    try {
      await customElements.whenDefined('theme-drawer');
    } catch {
      // Element may already be defined or unavailable.
    }
    drawer = document.querySelector('theme-drawer#cart-drawer');
  }

  return drawer;
}

/**
 * Opens the cart drawer without falling back to the cart page.
 * @returns {Promise<boolean>}
 */
async function openCartDrawer() {
  const drawer = await getCartDrawer();

  // Already open — do not call open() again. ThemeDrawer.#bringToFront
  // closes + re-shows the dialog, which flickers the cart shut/open.
  if (drawer?.isOpen || drawer?.hasAttribute('open')) {
    return true;
  }

  if (drawer && typeof drawer.open === 'function') {
    drawer.open();
    return true;
  }

  const dialog = /** @type {HTMLDialogElement | null} */ (
    document.querySelector('theme-drawer#cart-drawer dialog, #cart-drawer dialog')
  );
  if (dialog && typeof dialog.showModal === 'function' && !dialog.open) {
    dialog.showModal();
    return true;
  }

  return false;
}

/**
 * Toast host uses the Popover API so it sits in the top layer above
 * modal dialogs (quick-add, drawers, etc.). Falls back to a plain fixed div.
 * @returns {HTMLElement}
 */
function getToastHost() {
  let host = document.querySelector('.tw-cart-toast-host');
  if (host instanceof HTMLElement) return host;

  host = document.createElement('div');
  host.className = 'tw-cart-toast-host';
  host.setAttribute('aria-live', 'polite');
  host.setAttribute('aria-relevant', 'additions');
  if ('popover' in HTMLElement.prototype) {
    host.setAttribute('popover', 'manual');
  }
  document.body.appendChild(host);
  return host;
}

/**
 * @param {HTMLElement} host
 */
function openToastHost(host) {
  if (typeof host.showPopover === 'function' && host.hasAttribute('popover')) {
    try {
      if (!host.matches(':popover-open')) host.showPopover();
      return;
    } catch {
      // Fall through to class-only visibility.
    }
  }
}

/**
 * @param {HTMLElement} host
 */
function closeToastHost(host) {
  if (typeof host.hidePopover === 'function' && host.hasAttribute('popover')) {
    try {
      if (host.matches(':popover-open')) host.hidePopover();
    } catch {
      // Ignore hide failures during teardown.
    }
  }
}

/**
 * Ask the Shopify CDN for a thumbnail-sized rendition. Cart images come back as
 * the full master (1144px and up), which is a wasteful decode for a 64px box.
 * @param {string | null | undefined} url
 * @returns {string}
 */
function toastThumbUrl(url) {
  if (typeof url !== 'string' || !url) return '';
  const trimmed = url.startsWith('//') ? `https:${url}` : url;
  try {
    const parsed = new URL(trimmed, window.location.origin);
    if (!/(^|\.)shopify\.com$/.test(parsed.hostname) && !/cdn\.shop/.test(parsed.hostname)) return trimmed;
    parsed.searchParams.set('width', String(TOAST_THUMB_WIDTH));
    return parsed.toString();
  } catch {
    return trimmed;
  }
}

/**
 * @param {string} [message]
 * @param {{ error?: boolean, image?: string | null, imageAlt?: string }} [options]
 */
function showCartToast(message, options = {}) {
  const text = message || TOAST_MESSAGE;
  const now = Date.now();
  /*
    Dedup the same message only. Adding three different products in quick
    succession must still toast three times, but a single add that is reported
    by both the event promise and the caller must toast once.
  */
  if (text === lastToastMessage && now - lastToastAt < TOAST_DEDUP_MS) return;
  lastToastAt = now;
  lastToastMessage = text;

  window.clearTimeout(toastHideTimer);
  window.clearTimeout(toastRemoveTimer);

  const host = getToastHost();
  host.classList.remove('is-leaving');
  host.replaceChildren();

  const toast = document.createElement('div');
  toast.className = options.error ? 'tw-cart-toast tw-cart-toast--error' : 'tw-cart-toast';
  toast.setAttribute('role', options.error ? 'alert' : 'status');

  /*
    Product thumbnail when the added line gave us one, otherwise the dot. An
    image that 404s falls back to the dot rather than leaving a broken frame.
  */
  const thumbUrl = options.error ? '' : toastThumbUrl(options.image);
  /** @type {HTMLElement} */
  let marker;

  if (thumbUrl) {
    const thumb = document.createElement('img');
    thumb.className = 'tw-cart-toast__thumb';
    thumb.src = thumbUrl;
    thumb.alt = options.imageAlt || '';
    thumb.width = 64;
    thumb.height = 64;
    thumb.loading = 'eager';
    thumb.decoding = 'async';
    thumb.addEventListener(
      'error',
      () => {
        const dot = document.createElement('span');
        dot.className = 'tw-cart-toast__dot';
        dot.setAttribute('aria-hidden', 'true');
        thumb.replaceWith(dot);
      },
      { once: true }
    );
    marker = thumb;
  } else {
    const dot = document.createElement('span');
    dot.className = 'tw-cart-toast__dot';
    dot.setAttribute('aria-hidden', 'true');
    marker = dot;
  }

  // textContent, not innerHTML — the message can carry a product title.
  const label = document.createElement('span');
  label.className = 'tw-cart-toast__message';
  label.textContent = text;

  toast.append(marker, label);

  host.appendChild(toast);
  openToastHost(host);

  // Force a paint so the enter transition always runs, then reveal.
  void host.offsetWidth;
  window.requestAnimationFrame(() => {
    host.classList.add('is-visible');
  });

  toastHideTimer = window.setTimeout(() => {
    host.classList.add('is-leaving');
    host.classList.remove('is-visible');
    toastRemoveTimer = window.setTimeout(() => {
      closeToastHost(host);
      host.replaceChildren();
      host.classList.remove('is-leaving');
    }, TOAST_LEAVE_MS);
  }, TOAST_MS);
}

/**
 * Section ids the cart UI can morph. The drawer is rendered outside page
 * templates, so callers must request it explicitly.
 * @returns {string[]}
 */
function cartSectionIds() {
  const ids = [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((element) => element.getAttribute('data-section-id'))
    .filter((id) => typeof id === 'string' && id.length > 0);

  if (!ids.includes('cart-drawer-section')) ids.push('cart-drawer-section');
  return [...new Set(ids)];
}

/**
 * @param {string | number | undefined | null} value
 * @returns {string}
 */
function numericVariantId(value) {
  const text = String(value ?? '');
  const match = text.match(/(\d+)\s*$/);
  return match ? match[1] : text;
}

/**
 * Read the cookie cart and the HTML for every cart section.
 * POST /cart/update.js with only `sections` does not change line items.
 * @returns {Promise<Record<string, any>>}
 */
async function refreshCartFromAjax() {
  const body = new FormData();
  body.append('sections', cartSectionIds().join(','));
  body.append('sections_url', window.location.pathname);

  const response = await fetch(themeRoute('cart_update_url', '/cart/update.js'), {
    method: 'POST',
    body,
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });

  if (!response.ok) throw new Error('Unable to refresh cart');
  return response.json();
}

/**
 * GET the cookie cart. Cheaper than POST /cart/update.js, which re-renders
 * every cart section just to hand back a count.
 * @returns {Promise<Record<string, any>>}
 */
async function readCartJson() {
  const response = await fetch(`${themeRoute('cart_url', '/cart')}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error('Unable to read cart');
  return response.json();
}

/**
 * Morph already-rendered cart section HTML into the page. No network.
 * @param {Record<string, any> | null | undefined} sections
 * @returns {Promise<boolean>} whether anything was painted
 */
async function paintCartSections(sections) {
  const entries = Object.entries(sections && typeof sections === 'object' ? sections : {});
  let painted = false;

  await Promise.all(
    entries.map(async ([sectionId, html]) => {
      if (typeof html !== 'string' || !html.includes('shopify-section')) return;
      const host = document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);
      const mode = host?.hasAttribute('data-drawer') ? 'hydration' : 'full';
      try {
        await morphSection(sectionId, html, { mode });
        painted = true;
      } catch (error) {
        console.warn('[cart] Section sync failed:', error);
      }
    })
  );

  return painted;
}

/**
 * @returns {number}
 */
function readCartIconCount() {
  for (const icon of document.querySelectorAll('cart-icon')) {
    const count = icon.currentCartCount;
    if (typeof count === 'number' && Number.isFinite(count)) return count;
  }
  return 0;
}

/**
 * @param {number} itemCount
 */
function setCartIconCount(itemCount) {
  const next = Number(itemCount);
  if (!Number.isFinite(next) || next < 0) return;

  document.querySelectorAll('cart-icon').forEach((icon) => {
    if (typeof icon.renderCartBubble !== 'function') return;
    const current = typeof icon.currentCartCount === 'number' ? icon.currentCartCount : null;
    icon.renderCartBubble(next, current !== next);
  });
}

/**
 * Paint the cart drawer and header count.
 * Queued so an older response cannot overwrite a newer one.
 *
 * Pass `sections` — the HTML /cart/add.js already returned — to repaint with no
 * further request. Without it this falls back to reading the cart itself.
 *
 * @param {{
 *   sections?: Record<string, any> | null,
 *   itemCount?: number,
 *   reconcile?: boolean,
 * }} [options]
 * @returns {Promise<void>}
 */
function queueCartSync(options = {}) {
  const job = cartSyncQueue
    .catch(() => {})
    .then(async () => {
      if (await paintCartSections(options.sections)) {
        if (typeof options.itemCount === 'number') {
          setCartIconCount(options.itemCount);
          return;
        }
        /*
          The count was already bumped optimistically by the caller, so trueing
          it up stays off the critical path — an inventory cap or a cart script
          is corrected a moment later instead of delaying the confirmation.
          Callers whose line-update promise will deliver the authoritative total
          pass reconcile: false, so we do not read the cart twice over.
        */
        if (options.reconcile === false) return;
        readCartJson()
          .then((cart) => setCartIconCount(Number(cart.item_count) || 0))
          .catch(() => {});
        return;
      }

      const cart = await refreshCartFromAjax();
      await paintCartSections(cart.sections);
      setCartIconCount(Number(cart.item_count) || 0);
    });

  cartSyncQueue = job.catch((error) => {
    console.warn('[cart] Sync failed:', error);
  });

  return cartSyncQueue;
}

/**
 * @param {Array<{ merchandiseId?: string, id?: string, quantity?: number }> | undefined} lines
 * @returns {Promise<void>}
 */
async function applyAjaxLines(lines) {
  const items = [];

  for (const line of lines || []) {
    if (!line?.merchandiseId) continue;
    const quantity = Number(line.quantity);
    items.push({
      id: numericVariantId(line.merchandiseId),
      quantity: quantity > 0 ? quantity : 1,
    });
  }

  if (!items.length) return {};

  const response = await fetch(themeRoute('cart_add_url', '/cart/add.js'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      items,
      sections: cartSectionIds().join(','),
      sections_url: window.location.pathname,
    }),
  });

  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(result.description || result.message || 'Unable to add to cart');
  }
  return result;
}

/**
 * The lines an /cart/add.js response reports as added.
 *
 * A JSON `{ items: [...] }` add answers with `items`; a form-encoded add of a
 * single variant answers with the line item at the top level instead.
 * @param {Record<string, any> | null | undefined} result
 * @returns {Array<Record<string, any>>}
 */
function addedLines(result) {
  if (Array.isArray(result?.items)) return result.items;
  return result?.id ? [result] : [];
}

/**
 * @param {Record<string, any> | null | undefined} result
 * @returns {number}
 */
function addedQuantity(result) {
  return addedLines(result).reduce((total, item) => total + (Number(item?.quantity) || 0), 0);
}

/**
 * The added line, used for the toast thumbnail and title.
 * @param {Record<string, any> | null | undefined} result
 * @returns {Record<string, any> | null}
 */
function addedLine(result) {
  return addedLines(result)[0] ?? null;
}

/**
 * Shape an Ajax cart so Shopify.actions can resolve its line-update promises.
 * @param {Record<string, any>} cart
 */
function ajaxActionResult(cart) {
  return {
    cart: {
      totalQuantity: Number(cart.item_count) || 0,
      lines: {
        nodes: (cart.items || []).map((item) => ({
          id: item.key || String(item.id),
          quantity: item.quantity,
        })),
      },
      cost: {
        totalAmount: {
          amount: '0',
          currencyCode: cart.currency || 'INR',
        },
      },
    },
    detail: {
      sections: cart.sections,
      items: cart.items,
      itemCount: Number(cart.item_count) || 0,
      didError: false,
    },
  };
}

/**
 * Pending state for any add-to-cart control. Disabling the button is what stops
 * double submits; the class is only for styling.
 * @param {Element | null | undefined} target
 * @param {boolean} pending
 */
function setButtonPending(target, pending) {
  if (!(target instanceof Element)) return;
  const control = /** @type {HTMLButtonElement | null} */ (
    target instanceof HTMLButtonElement ? target : target.querySelector('button[name="add"], button[type="submit"]')
  );
  if (!control) return;

  control.disabled = pending;
  control.classList.toggle('is-cart-pending', pending);
  if (pending) {
    control.setAttribute('aria-busy', 'true');
  } else {
    control.removeAttribute('aria-busy');
  }
}

/**
 * Best-effort product name for the toast, read from whatever card or product
 * section the control sits in.
 * @param {Element | null | undefined} node
 * @returns {string}
 */
function productTitleFor(node) {
  if (node instanceof Element) {
    const card = node.closest('.tw-pdp-card, .tw-bestsellers-card, product-card, .card');
    const cardTitle = card?.querySelector('.tw-pdp-card__title, .tw-bestsellers-card__title, .card__heading, h3, h2');
    const text = cardTitle?.textContent?.trim();
    if (text) return text;
  }

  const pageTitle = document.querySelector('[data-tw-pdp-title], .tw-pdp-s__heading h1, .product-title, main h1');
  return pageTitle?.textContent?.trim() || '';
}

/**
 * @param {string} [productTitle]
 * @returns {string}
 */
function addedMessage(productTitle) {
  const title = (productTitle || '').trim();
  return title ? `${title} added to cart` : TOAST_MESSAGE;
}

/**
 * The single add-to-cart path for product cards, carousels and the PDP.
 *
 * Posts the line, then repaints every cart surface from the Ajax cart —
 * header bubble, cart drawer sections — and confirms with a toast carrying the
 * product name. The pending state is always cleared, including on failure.
 *
 * @param {string | number} variantId
 * @param {number} [quantity]
 * @param {{
 *   productTitle?: string,
 *   button?: Element | null,
 *   sellingPlanId?: string | number | null,
 *   properties?: Record<string, string>,
 * }} [options]
 * @returns {Promise<void>}
 */
async function addToCart(variantId, quantity = 1, options = {}) {
  const id = numericVariantId(variantId);
  if (!id) throw new Error('An add to cart request needs a variant id');

  const button = options.button ?? null;
  const qty = Number(quantity) > 0 ? Number(quantity) : 1;

  setButtonPending(button, true);

  try {
    /** @type {Record<string, any>} */
    const line = { id, quantity: qty };
    if (options.sellingPlanId) line.selling_plan = options.sellingPlanId;
    if (options.properties) line.properties = options.properties;

    const response = await fetch(themeRoute('cart_add_url', '/cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [line],
        sections: cartSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });

    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(result.description || result.message || 'Unable to add to cart');
    }

    /*
      The line is in the cookie cart the moment this resolves, so confirm now.
      The response already carries the product title and image, which beats
      scraping them out of whichever card the button sat in.
    */
    const added = addedLine(result);
    showCartToast(addedMessage(added?.product_title || options.productTitle || productTitleFor(button)), {
      image: added?.image || added?.featured_image?.url,
      imageAlt: added?.featured_image?.alt || '',
    });

    // Repaints from the section HTML in this same response — no second request.
    setCartIconCount(readCartIconCount() + (addedQuantity(result) || qty));
    await queueCartSync({ sections: result.sections });
  } catch (error) {
    showCartToast(error?.message || 'Could not add to cart', { error: true });
    throw error;
  } finally {
    setButtonPending(button, false);
  }
}

/**
 * @param {HTMLFormElement} form
 * @returns {Promise<void>}
 */
async function addFormToCart(form) {
  const formData = new FormData(form);
  const merchandiseId = formData.get('id');
  if (!merchandiseId) {
    // Never post a bare /cart/add without a variant — multi-variant cards are
    // meant to route to "Choose options" instead.
    throw new Error('This product needs options chosen before it can be added');
  }

  const button = form.querySelector('button[name="add"], button[type="submit"]');

  /** @type {Record<string, string>} */
  const properties = {};
  for (const [key, value] of formData.entries()) {
    const match = /^properties\[(.+)\]$/.exec(key);
    if (match && typeof value === 'string') properties[match[1]] = value;
  }

  await addToCart(merchandiseId, Number(formData.get('quantity')) || 1, {
    button,
    productTitle: productTitleFor(button || form),
    sellingPlanId: formData.get('selling_plan') || null,
    properties: Object.keys(properties).length ? properties : undefined,
  });
}

/**
 * ProductFormComponent reads the cart once more after a successful add so it
 * can true up quantity selectors. That read used to sit in front of the
 * confirmation, because the toast hung off the line-update promise which only
 * settles afterwards. It now announces the add as soon as /cart/add.js answers
 * and we confirm from here; the promise handler below stands down.
 */
function bindEarlyAdd() {
  document.addEventListener(EARLY_ADD_EVENT, (event) => {
    const detail = event instanceof CustomEvent && event.detail ? event.detail : {};
    const line = addedLine(detail);

    earlyAddAt = Date.now();
    showCartToast(addedMessage(line?.product_title || productTitleFor(detail.origin)), {
      image: line?.image || line?.featured_image?.url,
      imageAlt: line?.featured_image?.alt || '',
    });

    const quantity = addedQuantity(detail);
    if (quantity) setCartIconCount(readCartIconCount() + quantity);
    // ProductFormComponent is already reading the cart; its promise trues up
    // the count in alreadyAnnounced() below.
    queueCartSync({ sections: detail.sections, reconcile: false });
  });
}

/**
 * @param {Record<string, any> | null | undefined} detail
 * @returns {boolean}
 */
function alreadyAnnounced(detail) {
  if (detail?.didError) return false;
  if (detail?.source !== 'product-form-component') return false;
  return Date.now() - earlyAddAt < EARLY_ADD_WINDOW_MS;
}

function bindAddToast() {
  if (toastBound) return;
  toastBound = true;
  bindEarlyAdd();

  document.addEventListener(CART_LINES_UPDATE, (event) => {
    const promise = event.promise;
    /**
     * @param {Record<string, any>} [detail]
     */
    const sync = (detail) => {
      // Reuse the section HTML the add already fetched when it is on the event.
      queueCartSync({ sections: detail?.sections });
    };

    const isAdd = event?.action === 'add';
    /*
      ProductFormComponent dispatches this from the form host, so the button it
      came from is inside event.target. Drive the pending state from here and
      every add-to-cart in the theme gets double-submit protection and a
      guaranteed reset, whichever code path performed the add.
    */
    const origin = event.target instanceof Element ? event.target : null;
    const button = isAdd ? origin?.querySelector('button[name="add"], button[type="submit"]') : null;
    const title = isAdd ? productTitleFor(button || origin) : '';

    if (!promise || typeof promise.then !== 'function') {
      sync();
      if (isAdd) showCartToast(addedMessage(title));
      return;
    }

    if (button) {
      setButtonPending(button, true);
      // Last-resort release: a promise that never settles must not strand the
      // button, which is exactly how the stuck blank buttons were produced.
      const failsafe = window.setTimeout(() => setButtonPending(button, false), PENDING_FAILSAFE_MS);
      const release = () => {
        window.clearTimeout(failsafe);
        setButtonPending(button, false);
      };
      promise.then(release, release);
    }

    promise
      .then((result) => {
        const detail = result?.detail;

        /*
          bindEarlyAdd already toasted and repainted from the add response. Only
          the authoritative count is still worth taking from here.
        */
        if (isAdd && alreadyAnnounced(detail)) {
          const total = Number(result?.cart?.totalQuantity);
          if (Number.isFinite(total)) setCartIconCount(total);
          return;
        }

        sync(detail);
        if (!isAdd) return;
        if (detail?.didError) {
          showCartToast('Could not add to cart', { error: true });
          return;
        }
        showCartToast(addedMessage(title));
      })
      .catch((error) => {
        // The line may already be in the cookie cart even when the follow-up
        // read or Storefront call fails. Refresh from Ajax so a reload is not
        // required to see it.
        sync();
        if (error?.name !== 'AbortError') {
          console.warn('[cart] Add toast skipped:', error);
          if (isAdd) showCartToast('Could not add to cart', { error: true });
        }
      });
  });
}

function bindFormInterceptor() {
  if (formInterceptorBound) return;
  formInterceptorBound = true;

  // Catch native Shopify product forms that would otherwise POST to /cart.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const productFormHost = form.closest('product-form-component');
      if (productFormHost) {
        // ProductFormComponent normally owns submit handling. Keep this
        // interceptor as a fallback only when the custom element has not
        // upgraded yet (for example right after password-gate redirects).
        let isUpgraded = false;
        try {
          isUpgraded = productFormHost.matches(':defined');
        } catch {
          isUpgraded = false;
        }
        if (isUpgraded) return;
      }

      const action = (form.getAttribute('action') || '').toLowerCase();
      if (!action.includes('/cart/add')) return;

      event.preventDefault();
      event.stopPropagation();

      addFormToCart(form).catch((error) => {
        console.warn('[cart] Native add-to-cart failed:', error);
      });
    },
    true
  );
}

/**
 * @returns {boolean}
 */
function configureShopifyActions() {
  if (actionsConfigured) return true;

  const actions = window.Shopify?.actions;
  if (!actions?.updateCart || !actions?.openCart) return false;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('theme-drawer#cart-drawer') ?? document,
    /**
     * Storefront cart mutations 400 on this shop. Fall back to the Ajax cart
     * the theme actually renders, and include section HTML for the drawer.
     * @param {() => Promise<any>} perform
     * @param {{ lines?: Array<{ merchandiseId?: string, quantity?: number }> }} input
     */
    async handler(perform, input) {
      if (!storefrontCartBroken) {
        try {
          return await perform();
        } catch (error) {
          // Proven broken — do not pay for the failing round trip again.
          storefrontCartBroken = true;
          console.warn('[cart] Storefront cart update failed, using Ajax cart from here on:', error);
        }
      }

      const added = await applyAjaxLines(input?.lines);
      const cart = await readCartJson();
      if (added?.sections) cart.sections = added.sections;
      return ajaxActionResult(cart);
    },
  });

  // Explicit open only (header cart icon, etc.) — never auto-open after ATC.
  actions.openCart.configure({
    async handler() {
      await openCartDrawer();
    },
  });

  actionsConfigured = true;
  return true;
}

/**
 * @param {number} [attempt]
 */
function waitForShopifyActions(attempt = 0) {
  if (configureShopifyActions()) return;
  if (attempt >= ACTIONS_RETRY_MAX) return;
  window.setTimeout(() => waitForShopifyActions(attempt + 1), ACTIONS_RETRY_MS);
}

function init() {
  // Toast must bind even when Shopify.actions loads late (content_for_header).
  bindAddToast();
  bindFormInterceptor();
  waitForShopifyActions();
}

// Expose for other ATC scripts (also-bought, custom cards, etc.).
window.TorqWarsCart = {
  openDrawer: openCartDrawer,
  addToCart,
  addForm: addFormToCart,
  refresh: queueCartSync,
  toast: showCartToast,
};

// Toast binding does not depend on Shopify.actions — run as soon as possible.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
